"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Star, ThumbsUp, Share2, ExternalLink, BadgeCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GoogleReview() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="151139" id="googlereviews" className="bg-background relative flex min-h-[80vh] items-center justify-center overflow-hidden px-4 py-16 md:py-32">
      {}
      <div className="bg-primary/10 absolute top-1/2 left-1/2 -z-10 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px]" />

      <div ref={ref} className={`w-full max-w-2xl transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <div className="mb-12 text-center">
          <h2 className="text-foreground mb-4 font-serif text-4xl font-bold tracking-tight md:text-5xl">
            What Our Clients Say
          </h2>
          <p className="text-muted-foreground font-sans mx-auto max-w-lg text-lg">
            Discover why VIP Company is the preferred destination for professional beauty and styling services.
          </p>
        </div>

        <Card className="border-border bg-card/90 shadow-xl relative overflow-hidden rounded-xl border p-6 backdrop-blur-md md:p-10">
          {}
          <div className="absolute top-8 right-8 opacity-30 grayscale transition-all hover:opacity-100 hover:grayscale-0">
            <svg viewBox="0 0 24 24" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
          </div>

          {}
          <div className="mb-6 flex items-start gap-4">
            <SafeHydrate><Avatar className="size-14 border-2 border-border shadow-sm">
              <AvatarImage src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150" alt="Sophie de Vries" />
              <AvatarFallback className="bg-primary/20 text-primary font-bold">SV</AvatarFallback>
            </Avatar></SafeHydrate>
            <div>
              <h3 className="text-foreground font-sans text-lg font-semibold tracking-tight">
                Sophie de Vries
              </h3>
              <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                <span>2 weeks ago</span>
                <span className="bg-border h-1 w-1 rounded-full"></span>
                <span className="flex items-center gap-1">
                  <BadgeCheck className="text-primary size-3.5" />
                  Verified Review
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="mb-5 flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star, i) => <Star key={i} data-index={i} className="size-5 fill-[#FBBC05] text-[#FBBC05]" />)}
          </div>

          {}
          <div className="mb-8">
            <p className="text-foreground/90 font-sans text-base leading-relaxed md:text-lg md:leading-relaxed">
              VIP Company is truly a class apart. The attention to detail and the professional atmosphere make every visit a luxury experience. My hair has never looked better, and I always leave feeling pampered and confident. Highly recommended for anyone seeking top-tier beauty services and a personalized approach.
            </p>
          </div>

          {}
          <div className="mb-8 flex items-center gap-4">
            <Button variant="outline" size="sm" className="rounded-full border-border bg-background px-5 transition-all hover:bg-accent">
              <ThumbsUp className="text-muted-foreground mr-2 size-4" />
              <span className="text-foreground text-xs font-medium">Helpful</span>
            </Button>
            <Button variant="outline" size="sm" className="rounded-full border-border bg-background px-5 transition-all hover:bg-accent">
              <Share2 className="text-muted-foreground mr-2 size-4" />
              <span className="text-foreground text-xs font-medium">Share</span>
            </Button>
          </div>

          <Separator className="bg-border mb-8" />

          {}
          <div className="bg-muted/30 border-border relative rounded-lg border p-6">
            <div className="absolute top-0 left-0 h-full w-1 rounded-l-lg bg-primary/60"></div>
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-foreground font-sans text-sm font-bold">
                Response from VIP Company
              </h4>
              <span className="text-muted-foreground text-xs">1 week ago</span>
            </div>
            <p className="text-muted-foreground font-sans text-sm leading-relaxed">
              Thank you for your kind words, Sophie! We strive to provide every client with a true VIP experience, and we&apos;re delighted to hear you enjoyed your visit. We look forward to seeing you again soon for your next styling session!
            </p>
          </div>

          {}
          <div className="mt-10 flex justify-center">
            <Button asChild size="lg" className="group bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-10 font-medium shadow-lg transition-all">
              <Link to="https://share.google/OwolbHS7cPIK2C9Gc" newTab>
                View on Google Reviews
                <ExternalLink className="ml-2 size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </Card>
      </div>
    </section>;
}